import {
  createContext,
  useContext,
  useMemo,
  ReactNode,
  createElement,
} from 'react';
import { useAudiencesQuery } from '../../common/queries/use-audiences-query';
import { Audience } from './types';

type AudiencesContextType = {
  audiences: Audience[];
  isLoading: boolean;
  error: string | undefined;
};

const AudiencesContext = createContext<AudiencesContextType | undefined>(
  undefined
);

type AudiencesProviderProps = {
  children: ReactNode;
};

export function AudiencesProvider({ children }: AudiencesProviderProps) {
  const { data, isLoading, error } = useAudiencesQuery({ per_page: 10000 });

  const audiences = useMemo(
    () => (data?.data ?? []).map((resource) => resource.attributes),
    [data]
  );

  const value = useMemo(
    () => ({
      audiences,
      isLoading,
      error: error?.message,
    }),
    [audiences, isLoading, error]
  );

  return createElement(AudiencesContext.Provider, { value }, children);
}

export function useAudiences(): AudiencesContextType {
  const context = useContext(AudiencesContext);
  if (context === undefined) {
    throw new Error('useAudiences must be used within an AudiencesProvider');
  }
  return context;
}
